import {
  Injectable,
  Type,
  InjectionToken,
  AbstractType,
  Injector,
} from '@angular/core';
import {
  isFunction,
  get,
  head,
  isEmpty,
  map,
  toLower,
  set,
  size,
  reduce,
  filter,
  has,
  merge,
  isNil,
} from 'lodash';
import {
  PdfGenerateServiceV2,
  ViewSupportType,
} from '@app/shared/common/make-pdf/v2';
import { lazyInject } from '@app/containers/base/decorators';
import { buildInventoryCountPdfConfig } from './inventory-count-pdf-config';
import {
  IBaseEntityType,
  IInventoryItemVenue,
  IInventoryItemVenueMeasure,
  SiteForTransfer,
  ISite,
  USER_CONFIG_MODULE_NAME,
  USER_CONFIG_MODULE_TYPE,
} from '@app/shared/types';
import { rx } from '@app/shared/rx';
import { InventoryCountEditContainerComponent } from '@app/containers/inventory-counts/containers/edit/edit.inventory-count.container';

import { SitesService } from '../sites/sites.service';
import { ExportOptions } from './inventory-count.type';
import { StateService } from '../state-service';
import { dealInventoryCountPDFOptions } from './inventory-count.util';
import { sortAndFilterData } from '@app/shared/common/make-pdf/v2/utils';
import { ENTITY_ID_PATH } from '@config/global-constants';
import { XVII18nService } from '@app/i18n/services';

@Injectable()
export class InventoryCountPdfService {
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateServiceV2: PdfGenerateServiceV2;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;

  public constructor(public injector: Injector) {}

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public async handlePdfClick(
    editor: InventoryCountEditContainerComponent,
    options: ExportOptions,
  ): Promise<void> {
    const newOptions = dealInventoryCountPDFOptions(
      get(
        this.stateService.getUserConfig(
          USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
          USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
        ),
        'module_config.alphabetical_list_view',
        [],
      ),
      options,
    );
    const pdfConfig = buildInventoryCountPdfConfig(
      async () =>
        this.getPdfData(
          merge({}, editor.form.value, {
            name: get(editor, ['inventoryCountGroup', 'name']),
          }),
          {
            ...newOptions,
            isFromDetail: true,
            xuxGrid: editor.entityFormService.listGrid,
          },
        ),
      { ...newOptions, i18nService: this.i18nService },
      { isFromDetail: true },
    );

    await this.pdfGenerateServiceV2.downloadPdfAsync(pdfConfig);
  }

  /**
   * get site info
   *
   * @param baseOnDoc base on doc
   * will return site_info data if you pass site_info field(no empty)
   * otherwise will use site_id to get latest site info
   */
  public async getSiteInfo(
    baseOnDoc: IBaseEntityType,
  ): Promise<SiteForTransfer | ISite | null> {
    if (!isEmpty(baseOnDoc.site_info)) {
      return baseOnDoc.site_info;
    }

    const siteId = baseOnDoc.site_id;

    if (isEmpty(siteId)) {
      return null;
    }

    const sitesInfo = await this.sitesService
      .fetchList({
        filter: `_id eq in(${siteId})`,
        select: ['name', 'id', 'store_number'],
        filter_by_assigned_sites: true,
      })
      .pipe(rx.first())
      .toPromise();

    return head(sitesInfo.items);
  }

  /**
   * get item measures(with counts data)
   *
   * @param item flat inventory item data -- flat by @method this.flattenItems
   * @param useChildrenData is need get measure from children fields
   * (handle those data which come from inventory count detail page)
   */
  public getItemMeasures(
    item: IInventoryItemVenue,
    useChildrenData: boolean = false,
    isAlphabetical: boolean = false,
  ): IInventoryItemVenueMeasure[] {
    if (isAlphabetical) {
      return item?.measures || item?.measure;
    }

    return useChildrenData
      ? map(item?.children, (count) => {
          return {
            measure_description: count.description,
            measure_entity_id: count.entity_id,
            count: count.count,
            factor_value: count.factor_value,
          };
        })
      : item?.measures || item?.measure;
  }

  public async getPdfData(
    doc: IBaseEntityType,
    option: ExportOptions & { isFromDetail: boolean },
  ): Promise<IBaseEntityType> {
    const { isFromDetail = true, xuxGrid, mode } = option;
    const siteInfo = await this.getSiteInfo(doc);
    const gridFilterString = get(
      xuxGrid,
      'xuxGridComponent.actionPanel.quickFilterControl.value',
    );
    // init pdf doc
    const document: IBaseEntityType = {
      gridFilterString,
      siteInfo,
      inventoryCountName: doc.name,
    };
    const isAlphabetical = mode === 'alphabetical';
    const inventoryItems = doc.inventory_count_items;
    // eslint-disable-next-line no-nested-ternary
    const flattenCountItems = !isEmpty(inventoryItems)
      ? isAlphabetical
        ? filter(inventoryItems, (item) => !has(item, 'isLeaf'))
        : this.flattenItems(inventoryItems)
      : [];
    // deal the to be printed out items
    const toBePrintedItems = isFromDetail
      ? filter(
          sortAndFilterData(
            flattenCountItems,
            {
              baseOnGrid: xuxGrid,
              viewSupport: ViewSupportType.sortAndFilter,
            },
            ENTITY_ID_PATH,
            ENTITY_ID_PATH,
          ),
          (data) => !isNil(data),
        )
      : flattenCountItems;
    const virtualInventoryItems = map(toBePrintedItems, (item) => {
      // covert uom list to object like { uomDes_0: xxx, uomCount_0: xxx, uomFactorValue_0: xxx, uomDes_1: xxx ... }
      const uomFields = reduce(
        this.buildUomItems(
          this.getItemMeasures(item, isFromDetail, isAlphabetical),
        ),
        (totalUomFields, curValue, index) => ({
          ...totalUomFields,
          [`uomDes_${index}`]: curValue.uomDes,
          [`uomCount_${index}`]: curValue.uomCount,
          [`uomFactorValue_${index}`]: curValue.uomFactorValue,
        }),
        {},
      );

      const majorCategoryName = get(
        item,
        'reporting_category_major.name',
        item.major_category_name ?? '',
      );
      const minorCategoryName = get(
        item,
        'reporting_category_minor.name',
        item.minor_category_name ?? '',
      );

      return {
        ...item,
        ...uomFields,
        major_category_name: majorCategoryName,
        minor_category_name: minorCategoryName,
        sortIdentity: `${toLower(majorCategoryName)}_${toLower(minorCategoryName)}_${toLower(get(item, 'name', ''))}`,
      };
    });

    set(document, 'virtual.inventory_count_items', virtualInventoryItems);

    return document;
  }

  /**
   * build uom items through measure data
   *
   * @param measures base on measures data
   * @param maxLimit max uom count @default 3
   */
  public buildUomItems(
    measures: (IInventoryItemVenueMeasure & {
      measure_description?: string;
      count?: number;
    })[],
    maxLimit: number = 3,
  ): {
    uomDes: string;
    uomCount: number;
    uomFactorValue: number;
  }[] {
    if (isEmpty(measures)) {
      return [];
    }

    const uomItems: {
      uomDes: string;
      uomCount: number;
      uomFactorValue: number;
    }[] = [];

    do {
      const curUomItemIndex = size(uomItems);

      const curMeasure = measures[curUomItemIndex];

      const uomItem = { uomDes: null, uomCount: null, uomFactorValue: null };

      if (!isEmpty(curMeasure)) {
        uomItem.uomDes = '';
        uomItem.uomFactorValue = curMeasure.factor_value;

        if (!isEmpty(curMeasure.measure_description)) {
          uomItem.uomDes = curMeasure.measure_description;
        }
        if (!isEmpty(curMeasure.description)) {
          uomItem.uomDes = curMeasure.description;
        }

        uomItem.uomCount = curMeasure.count ?? uomItem.uomCount;
      }

      uomItems.push(uomItem);
    } while (size(uomItems) < maxLimit);

    return uomItems;
  }

  /**
   * u can use this to flatten your inventory count detail page tree items
   *
   * @param treeData target tree inventory items
   */
  public flattenItems(treeData = []): IInventoryItemVenue[] {
    return treeData.reduce(
      (list, { children, ...item }): IInventoryItemVenue[] => {
        if (item?.site_id) list.push({ ...item, children });
        if (children) list.push(...this.flattenItems(children));

        return list;
      },
      [],
    );
  }
}
